const _ = require("lodash");
const React = require("react");
const {augmentHOCWrapper, getBaseInstance} = require("../util/HOCUtil");
const VaultContext = require("../top/VaultContext").default;

// Recreating Derived.isLoggedIn because of an extremely weird require chain
// from src/admin/main to src/core/Bundles. This was the easiest link to
// break. Do not require things like Derived in this file (customlint will
// check this for you).
const isLoggedIn = vaultState => {
	return !!_.get(vaultState, "user.userID");
};

// getPropsFromVault is a function taking (props, vaultState) and returning
// an object that is added as extra props to the wrapped component. Note that
// getPropsFromVault is called with the global variable as the this value.
module.exports = augmentHOCWrapper(function(
	Component,
	getPropsFromVault,
	options
) {
	const {forLoggedIn = false} = options || {};

	// Some views are only rendered for logged in users. Set forLoggedIn on
	// options to prevent them from updating when the vault is not logged in.
	if (forLoggedIn && getPropsFromVault) {
		const origPropsFromVault = getPropsFromVault;
		getPropsFromVault = (props, vaultState) => {
			return !isLoggedIn(vaultState)
				? null
				: origPropsFromVault(props, vaultState);
		};
	}

	class VaultUpdateWrapper extends React.Component {
		static displayName = "VaultUpdate:" + Component.displayName;
		static contextType = VaultContext;

		constructor(props, context) {
			super(props);

			const vault = context;
			this.lastStateGeneration = vault.getGeneration();
			this.lastEventsGeneration = -Infinity;
			this.unmounted = false;
			this.cachedBaseInstance = null;

			//Must default to {} if getPropsFromVault returns null to signal logged out and no update.
			this.state = getPropsFromVault
				? getPropsFromVault(this.props, vault.getState()) || {}
				: {};
		}

		/** Helper functions */

		// get the wrapped component (components may be wrapped in multiple HOCs)
		getBaseInstance = () => {
			this.cachedBaseInstance =
				this.cachedBaseInstance || getBaseInstance(this);
			return this.cachedBaseInstance;
		};

		// Pass a return of getPropsFromVault. Compares it to the values in the state
		// of the view now and returns whether anything changed.
		hasStateChanges = newPropsFromVault => {
			return _.some(
				newPropsFromVault,
				(value, key) => this.state[key] !== value
			);
		};

		onVaultUpdate = (newVaultState, generation, customEvents) => {
			this.setStateFromVault(null, newVaultState, generation);
			this.handleVaultCustomEvents(customEvents, generation);
		};

		// props is optional and defaults to this.props. If provided, the generation
		// comparison is skipped.
		setStateFromVault = (props, newVaultState, generation) => {
			if (this.unmounted || !getPropsFromVault) {
				return null;
			}
			if (!props && this.lastStateGeneration >= generation) {
				return null;
			}

			this.lastStateGeneration = generation;

			const newPropsFromVault = getPropsFromVault(
				props || this.props,
				newVaultState
			);
			// Allow getPropsFromVault to return falsy to suppress updates. This is
			// useful in views that know they're about to get unmounted.
			if (newPropsFromVault && this.hasStateChanges(newPropsFromVault)) {
				this.setState(newPropsFromVault);
			}
		};

		// trigger corresponding event handlers in the base component when vault
		// events are emitted.
		handleVaultCustomEvents = (customEvents, generation) => {
			if (this.unmounted) {
				return;
			}
			const baseInst = this.getBaseInstance();
			const eventHandlers = _.get(baseInst, "vaultEvents");
			if (!eventHandlers) {
				return;
			}
			if (this.lastEventsGeneration >= generation) {
				return;
			}
			this.lastEventsGeneration = generation;

			if (!customEvents || !customEvents.length) {
				return;
			}
			setTimeout(
				() =>
					customEvents.forEach(event => {
						if (this.unmounted) {
							return;
						}
						const handler = eventHandlers[event.name];
						handler && handler.apply(baseInst, event.args);
					}),
				0
			);
		};

		UNSAFE_componentWillReceiveProps(newProps) {
			const vault = this.context;
			if (!vault) {
				return null;
			}
			this.setStateFromVault(newProps, vault.getState(), vault.getGeneration());
		}

		componentDidMount() {
			const vault = this.context;
			vault.on("update", this.onVaultUpdate);
			this.setStateFromVault(null, vault.getState(), vault.getGeneration());
			this.handleVaultCustomEvents(
				vault.getLastCustomEvents(),
				vault.getGeneration()
			);
		}

		componentDidUpdate() {
			const vault = this.context;
			this.handleVaultCustomEvents(
				vault.getLastCustomEvents(),
				vault.getGeneration()
			);
		}

		componentWillUnmount() {
			this.unmounted = true;
			const vault = this.context;
			vault.off("update", this.onVaultUpdate);
		}

		render() {
			return <Component {...this.props} {...this.state} />;
		}
	}

	return VaultUpdateWrapper;
});
